import MixpanelTracker from "./mixpanel-tracker";

interface FWError {
  code: ErrorCode;
  extra?: {
    props?: Array<string>;
    serverError?: any;
  };
}

export enum FWFunction {
  realm = "realm",
  irra = "irra",
  irr = "irr",
  assetAccounts = "asset accounts",
  assetAccount = "asset account",
  accountCodes = "account codes",
  variables = "variables",
  variableValue = "variable values",
  investorValue = "investor values",
  asset = "asset",
  assetUpdateTypes = "asset update types",
  submissionUpdateTypes = "submission update types",
  assetUpdates = "asset updates",
  submissionUpdates = "submission updates",
  assets = "assets",
  underlyingAssets = "underlying assets",
  metrics = "metrics",
  metricValue = "metric value",
  fund = "fund",
  fundUpdateTypes = "fund update types",
  fundUpdates = "fund updates",
  funds = "funds",
  investor = "investor",
  investors = "investors",
  noticeGroups = "notice groups",
  vendor = "vendor",
  vendors = "vendors",
  leads = "leads",
  lead = "lead",
  journalLines = "journal lines",
  fundTransactions = "fund transactions",
  fundTransactionTypes = "fund transaction types",
  properties = "properties",
  deals = "deals",
  dealsCount = "deals count",
  dealNotes = "deal notes",
  round = "round",
  rounds = "rounds",
  captables = "captables",
  shareholders = "shareholders",
  fdStake = "fdStake",
  raised = "amount raised",
  preMoney = "pre-money valuation",
  postMoney = "post-money valuation",
  valuation = "valuation",
  forecastScenarios = "forecast scenarios",
  forecastRounds = "forecast rounds",
  fxRate = "fx rate",
  metricCategories = "metric categories",
  investorTransactions = "investor transactions",
  investorTransactionTypes = "investor transaction types",
  activity = "activity",
  ai = "AI",
  mcp = "mcp"
}

export enum ErrorCode {
  MissingTeamUrl = 4001,
  MissingOneOfProps = 4002,
  MissingRequiredProps = 4003,
  DataNotFound = 4004,
  ServerError = 5001
}

const functionTeamMap: Record<string, Array<FWFunction>> = {
  dealflow: [FWFunction.deals, FWFunction.dealNotes, FWFunction.dealsCount]
};

export default class ErrorHandlerUtil {
  static errorHandler(error: any, functionName: FWFunction, cellAddress?: string, params?: any) {
    console.error(error, functionName);
    const serverError: string | undefined = ErrorHandlerUtil.#handleServerError(error);

    MixpanelTracker.trackFunction({ functionName, status: "failure", cellAddress, error });

    let missingProps: Array<string> = [];
    if (!!params) {
      Object.keys(params).forEach((param) => {
        if (!params[param]) missingProps.push(param);
      });
    }

    if (!Boolean(missingProps.length)) {
      missingProps =
        (error === ErrorCode.MissingOneOfProps ? ErrorHandlerUtil.#getRequiredProps(functionName).oneOf : ErrorHandlerUtil.#getRequiredProps(functionName).required) || [];
    }

    const fwError: FWError = {
      code: typeof error === "number" ? error : ErrorCode.ServerError,
      extra: {
        props: missingProps,
        serverError: serverError || "Error"
      }
    };

    return ErrorHandlerUtil.#handleFwError(fwError, functionName);
  }

  static #handleServerError(error: any) {
    let serverError: string | undefined;
    const serverErrorConstants = ["Access Denied", "Access is denied", "Permission Denied"];

    if (serverErrorConstants.includes(error) || error.toString().startsWith("Couldn't fetch")) serverError = error;

    return serverError;
  }

  static #handleFwError(error: FWError, functionName: FWFunction) {
    let team: string = "fundwave";

    Object.keys(functionTeamMap).forEach((teamName: string) => {
      if (functionTeamMap[teamName].includes(functionName)) team = teamName;
    });

    switch (error.code) {
      case ErrorCode.MissingTeamUrl:
        return [[`Please select a ${team} team from sidebar`]];
      case ErrorCode.MissingOneOfProps:
        return [[`Please enter atleast one of ${error.extra?.props?.join(" or ")}`]];
      case ErrorCode.MissingRequiredProps:
        return [[`Please input the required fields - ${error.extra?.props?.join(", ")}`]];
      case ErrorCode.DataNotFound:
        return [[`No ${functionName} found`]];
      case ErrorCode.ServerError:
        const err = error.extra?.serverError;
        return [[err]];
      default:
        return [["Error"]];
    }
  }

  static #getRequiredProps(functionName: FWFunction): { oneOf?: Array<string>; required?: Array<string> } {
    switch (functionName) {
      case FWFunction.irra:
        return { required: ["assetId", "irrDate"] };
      case FWFunction.irr:
        return { oneOf: ["assetIds", "fundIds"] };
      case FWFunction.assetAccounts:
        return { oneOf: ["assetIds", "fundIds"] };
      case FWFunction.assetAccount:
        return { required: ["assetAccountId"] };
      case FWFunction.accountCodes:
        return { required: ["fundId"] };
      case FWFunction.variables:
        return { required: ["fundId"] };
      case FWFunction.variableValue:
        return { required: ["variableCodes"], oneOf: ["assetIds", "investorCodes", "fundIds", "assetAccountIds"] };
      case FWFunction.investorValue:
        return { required: ["variableCodes", "investorCodes", "fundIds"] };
      case FWFunction.asset:
        return { required: ["assetId"] };
      case FWFunction.underlyingAssets:
        return { oneOf: ["parentAssetIds", "fundId"] };
      case FWFunction.assetUpdates:
        return { required: ["assetId", "toDate"] };
      case FWFunction.submissionUpdates:
        return { required: ["assetId", "toDate"] };
      case FWFunction.metrics:
        return { required: ["assetId"] };
      case FWFunction.metricValue:
        return { required: ["assetIds", "metricCodes", "toDate"] };
      case FWFunction.fund:
        return { required: ["fundId"] };
      case FWFunction.fundUpdates:
        return { required: ["fundId", "toDate"] };
      case FWFunction.investor:
        return { required: ["investorId"] };
      case FWFunction.noticeGroups:
        return { required: ["fundId"] };
      case FWFunction.journalLines:
        return { oneOf: ["accountCodes", "assetIds", "assetAccountIds", "investorIds", "fundIds"] };
      case FWFunction.fundTransactions:
        return { oneOf: ["fundIds", "assetIds", "assetAccountIds"] };
      case FWFunction.fundTransactionTypes:
        return { required: ["fundId"] };
      case FWFunction.properties:
        return { required: ["entity"] };
      case FWFunction.dealsCount:
        return { required: ["properties"] };
      case FWFunction.dealNotes:
        return { required: ["dealIds", "toDate"] };
      case FWFunction.round:
        return { required: ["assetId"] };
      case FWFunction.rounds:
        return { required: ["assetId"] };
      case FWFunction.fdStake:
        return { required: ["assetId", "fundDetail"] };
      case FWFunction.valuation:
        return { oneOf: ["assetIds", "fundIds"] };
      case FWFunction.raised:
        return { required: ["assetIds", "displayCurrency"] };
      case FWFunction.preMoney:
        return { required: ["assetIds", "displayCurrency"] };
      case FWFunction.postMoney:
        return { required: ["assetIds", "displayCurrency"] };
      case FWFunction.forecastRounds:
        return { oneOf: ["captableId", "assetId"] };
      case FWFunction.fxRate:
        return { required: ["baseCurrency", "toCurrency", "date"] };
      case FWFunction.investorTransactions:
        return { required: ["fundId"]};
      case FWFunction.investorTransactionTypes:
        return { required: ["fundId"]};
      case FWFunction.ai:
        return { required: ["question"]};
      case FWFunction.mcp:
        return { required: ["question"]};
      case FWFunction.activity:
        return { oneOf: ["fundIds", "investorIds", "assetIds", "toDate", "fromDate",]};
      default:
        return {};
    }
  }
}
